import { css } from 'styled-components';

/* ============================================================
   flowKit — shared foundation for the ported v2 flows
   (onboarding, course wizard, workflow map).

   The reference flow CSS is written entirely against CSS custom
   properties (var(--accent), var(--surface)…). Rather than rewrite
   every rule into ${({theme})=>…} interpolations (error-prone), we
   re-declare those custom properties from the styled-components
   theme on each flow's root wrapper, then paste the reference CSS
   verbatim. Output is pixel-identical and stays theme-reactive.
   ============================================================ */

// Re-publish the theme tokens as the reference's CSS variable names.
export const themeVars = css`
  --bg: ${({ theme }) => theme.bg};
  --surface: ${({ theme }) => theme.surface};
  --surface-2: ${({ theme }) => theme.surface2};
  --surface-3: ${({ theme }) => theme.surface3};
  --border: ${({ theme }) => theme.border};
  --border-strong: ${({ theme }) => theme.borderStrong};
  --text: ${({ theme }) => theme.text};
  --text-muted: ${({ theme }) => theme.textMuted};
  --text-subtle: ${({ theme }) => theme.textSubtle};
  --accent: ${({ theme }) => theme.accent};
  --accent-strong: ${({ theme }) => theme.accentStrong};
  --accent-soft: ${({ theme }) => theme.accentSoft};
  --accent-line: ${({ theme }) => theme.accentLine};
  --on-accent: ${({ theme }) => theme.onAccent};
  --rail-bg: ${({ theme }) => theme.railBg};
  --success: ${({ theme }) => theme.success};
  --warning: ${({ theme }) => theme.warning};
  --danger: ${({ theme }) => theme.danger};
  --info: ${({ theme }) => theme.info};
  --r-sm: ${({ theme }) => theme.rSm};
  --r-md: ${({ theme }) => theme.rMd};
  --r-lg: ${({ theme }) => theme.rLg};
  --r-xl: ${({ theme }) => theme.rXl};
  --r-pill: ${({ theme }) => theme.rPill};
  --shadow-sm: ${({ theme }) => theme.shadowSm};
  --shadow-md: ${({ theme }) => theme.shadowMd};
  --shadow-lg: ${({ theme }) => theme.shadowLg};
  --ring-1: ${({ theme }) => theme.ring1};
  --card-shadow: ${({ theme }) => theme.cardShadow};
  --card-border: ${({ theme }) => theme.cardBorder};
  --btn-bg: ${({ theme }) => theme.btnBg};
  --btn-bg-hover: ${({ theme }) => theme.btnBgHover};
  --btn-fg: ${({ theme }) => theme.btnFg};
  --font-body: ${({ theme }) => theme.fontBody};
  --font-head: ${({ theme }) => theme.fontHead};
  --space: ${({ theme }) => theme.space};
  --gap: ${({ theme }) => theme.gap};
  --pad-card: ${({ theme }) => theme.padCard};
  --row-h: ${({ theme }) => theme.rowH};
  --control-h: ${({ theme }) => theme.controlH};
  --fs-base: ${({ theme }) => theme.fsBase};
  --fs-sm: ${({ theme }) => theme.fsSm};
  --fs-micro: ${({ theme }) => theme.fsMicro};

  * , *::before, *::after { box-sizing: border-box; }
`;

// Verbatim reference component classes the flows lean on (block 2 of the
// reference design system). Kept self-contained so the flows render correctly
// no matter where in the DOM they mount (overlays are position:fixed).
export const flowComponents = css`
  .btn {
    display: inline-flex; align-items: center; justify-content: center; gap: 8px;
    height: var(--control-h); padding: 0 16px; border-radius: var(--r-md);
    font-size: var(--fs-sm); font-weight: 650; cursor: pointer; white-space: nowrap;
    border: 1px solid transparent;
    transition: background .15s, border-color .15s, box-shadow .15s, transform .08s;
    text-decoration: none;
  }
  .btn:active { transform: translateY(1px); }
  .btn svg { width: 16px; height: 16px; }
  .btn--primary { background: var(--btn-bg); color: var(--btn-fg); box-shadow: 0 1px 2px rgba(17,20,38,.18), inset 0 1px 0 rgba(255,255,255,.12); }
  .btn--primary:hover { background: var(--btn-bg-hover); box-shadow: 0 2px 8px -2px rgba(17,20,38,.4), inset 0 1px 0 rgba(255,255,255,.12); }
  .btn--ghost { background: var(--surface); border-color: var(--border); color: var(--text); box-shadow: var(--shadow-sm); }
  .btn--ghost:hover { border-color: var(--border-strong); background: var(--surface-2); }
  .btn--subtle { background: var(--surface-3); color: var(--text); }
  .btn--subtle:hover { background: var(--surface-2); border-color: var(--border); }
  .btn--sm { height: 32px; padding: 0 12px; font-size: var(--fs-micro); }
  .btn--danger { background: color-mix(in oklch, var(--danger) 12%, var(--surface)); color: var(--danger); }
  .btn--danger:hover { background: color-mix(in oklch, var(--danger) 20%, var(--surface)); }
  .btn[disabled] { cursor: not-allowed; }

  .tag {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 3px 9px; border-radius: var(--r-pill); font-size: var(--fs-micro); font-weight: 700;
    box-shadow: inset 0 0 0 1px color-mix(in oklch, currentColor 20%, transparent);
  }
  .tag--green  { color: var(--success); background: color-mix(in oklch, var(--success) 14%, var(--surface)); }
  .tag--amber  { color: oklch(0.58 0.13 60); background: color-mix(in oklch, var(--warning) 18%, var(--surface)); }
  .tag--gray   { color: var(--text-muted); background: var(--surface-3); }
  .tag--accent { color: var(--accent-strong); background: var(--accent-soft); }
  .tag--red    { color: var(--danger); background: color-mix(in oklch, var(--danger) 13%, var(--surface)); }
  .tag--blue   { color: var(--info); background: color-mix(in oklch, var(--info) 14%, var(--surface)); }

  .icon-btn {
    width: var(--control-h); height: var(--control-h); flex: none;
    display: grid; place-items: center; border-radius: var(--r-md);
    background: var(--surface); border: 1px solid var(--border); color: var(--text-muted);
    cursor: pointer; transition: .15s; position: relative; box-shadow: var(--shadow-sm);
  }
  .icon-btn:hover { color: var(--text); background: var(--surface-2); border-color: var(--border-strong); }

  /* form controls */
  .field { margin-bottom: 16px; }
  .field > label { display: block; font-size: var(--fs-sm); font-weight: 650; margin-bottom: 6px; color: var(--text); }
  .input, .select, .textarea {
    width: 100%; height: var(--control-h); padding: 0 13px; border-radius: var(--r-md);
    border: 1px solid var(--border); background: var(--surface-2); color: var(--text);
    font-size: 13.5px; font-family: var(--font-body); outline: none; transition: .15s;
  }
  .textarea { height: auto; padding: 11px 13px; resize: vertical; min-height: 90px; }
  .input:focus, .select:focus, .textarea:focus {
    border-color: var(--accent); background: var(--surface);
    box-shadow: 0 0 0 3px var(--accent-soft);
  }
  .form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }

  /* switch */
  .switch {
    width: 42px; height: 24px; border-radius: 999px; background: var(--surface-3);
    border: 1px solid var(--border); position: relative; cursor: pointer; flex: none; transition: .18s;
  }
  .switch::after {
    content: ""; position: absolute; top: 2px; left: 2px; width: 18px; height: 18px;
    border-radius: 50%; background: #fff; box-shadow: var(--shadow-sm); transition: .18s;
  }
  .switch.on { background: var(--accent); border-color: var(--accent); }
  .switch.on::after { left: 20px; }

  /* settings row */
  .set-row { display: flex; align-items: center; justify-content: space-between; gap: 20px; }
  .set-row__txt h4 { margin: 0; font-size: 14px; font-weight: 650; }
  .set-row__txt p { margin: 3px 0 0; font-size: var(--fs-sm); color: var(--text-muted); max-width: 52ch; }

  /* inline assistant note */
  .actas {
    display: flex; align-items: center; gap: 10px; padding: 10px 16px;
    background: var(--accent-soft); border: 1px solid var(--accent-line);
    border-radius: var(--r-md); font-size: var(--fs-sm); color: var(--text);
  }
  .actas b { color: var(--accent-strong); }

  /* logo tile used in flow rails / brand */
  .rail__logo {
    width: 38px; height: 38px; border-radius: 11px; flex: none; display: grid; place-items: center;
    background: linear-gradient(150deg, var(--accent), var(--accent-strong));
    color: var(--on-accent); font-family: var(--font-head); font-weight: 800; font-size: 18px;
  }
`;

/* shared spin keyframe used by the wizard / stripe redirect */
export const flowKeyframes = css`
  @keyframes ob-spin { to { transform: rotate(360deg); } }
`;
